import { TEAL_3CE } from "@frontend/palette";
import { ChevronRight, EditNote, People } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Drawer,
  IconButton,
  List,
  Typography,
} from "@mui/material";
import React from "react";
import { ArrowsIcon, HomeIcon } from "../../assets/vectors";
import SidebarItem, { SidebarItemType } from "./SidebarItem";

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 80;

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

const mainNavItems = [
  { label: "Home", icon: HomeIcon, id: "Home", href: "/" },
  {
    label: "Customers",
    icon: People,
    id: "Customers",
    href: "/customers",
    urlPattern: /^\/customers(\/.*)?$/,
  },
];

const ratesItems = [
  {
    label: "Rates CMS",
    icon: EditNote,
    id: "rates",
    href: "/bellarates",
    urlPattern: /^\/bellarates(\/.*)?$/,
  },
];

const Sidebar: React.FC<SidebarProps> = ({ open, onToggle }) => {
  const NavToggleIcon = () => (
    <Box
      aria-hidden="true"
      sx={{
        display: "inline-flex",
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.3s ease",
      }}
    >
      <ArrowsIcon size={20} strokeColor="black" />
    </Box>
  );

  const navButton: SidebarItemType = {
    label: "",
    icon: NavToggleIcon,
    id: "nav",
    onClick: onToggle,
    ariaExpanded: open,
  };

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
        flexShrink: 0,
        mt: "88px",
        "& .MuiDrawer-paper": {
          width: open ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
          boxSizing: "border-box",
          borderRight: "1px solid rgba(42, 39, 39, 0.12)",
          transition: "width 0.3s ease",
          overflowX: "hidden",
          position: "relative",
          zIndex: (theme) => theme.zIndex.drawer,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <List sx={{ px: 1, pt: 2 }}>
          <SidebarItem key={"nav"} item={navButton} isSidebarOpen={open} />
        </List>

        {/* Main Navigation */}
        <List sx={{ px: 1 }}>
          {mainNavItems.map((item) => (
            <SidebarItem key={item.id} item={item} isSidebarOpen={open} />
          ))}
        </List>

        {/* Rates CMS Section */}
        {open && (
          <Typography
            variant="caption"
            sx={{
              px: 2,
              py: 1,
              color: "text.secondary",
              fontWeight: 600,
              textTransform: "uppercase",
              fontSize: "0.7rem",
            }}
          >
            Rates CMS
          </Typography>
        )}
        <List sx={{ px: 1 }}>
          {ratesItems.map((item) => (
            <SidebarItem key={item.id} item={item} isSidebarOpen={open} />
          ))}
        </List>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* User Profile Section */}
        <Box
          sx={{
            borderTop: "1px solid rgba(0, 0, 0, 0.12)",
            p: 2,
          }}
        >
          {open ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar
                sx={{
                  bgcolor: TEAL_3CE,
                  width: 40,
                  height: 40,
                  fontSize: "0.9rem",
                }}
              >
                BT
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Bob Teller
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                >
                  bob@Ccce.com
                </Typography>
              </Box>
              <IconButton size="small" aria-label="Account menu">
                <ChevronRight aria-hidden="true" />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Avatar
                sx={{
                  bgcolor: TEAL_3CE,
                  width: 40,
                  height: 40,
                  fontSize: "0.9rem",
                }}
              >
                BT
              </Avatar>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
